import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  agentId?: string;
  agentName?: string;
  isDelegated?: boolean;
  metadata?: {
    latency?: number;
    taskStatus?: string;
    thinkingPreview?: string;
  };
}

export interface AttachmentRef {
  's3-key': string;
  'content-type': string;
  filename: string;
}

export interface ChatRequest {
  query: string;
  agentId: string;
  sessionId?: string;
  forceNewSession?: boolean;
  environment?: 'sandbox' | 'production';
  attachments?: AttachmentRef[];
}

export interface ChatResponse {
  sessionId: string;
  message: ChatMessage;
  thinking?: string;
  conversationId?: string;
  status?: 'processing' | 'completed' | 'failed';
}

export interface UploadUrlRequest {
  filename: string;
  'content-type': string;
}

export interface UploadUrlResponse {
  'upload-url': string;
  's3-key': string;
  'expires-in': number;
}

export interface VoiceTtsRequest {
  text: string;
  voice?: string;
  model?: string;
}

export interface VoiceTtsResponse {
  audio: string;
  'content-type': string;
  voice: string;
  model: string;
}

export interface VoiceSttRequest {
  audio: string;
  'content-type'?: string;
}

export interface VoiceSttResponse {
  text: string;
}

/**
 * Map the raw lumina-api chat response into the ChatResponse shape
 * the frontend component expects.
 *
 * Raw response from the API:
 *   { response, session_id, agent_name, exchange_id, state, metadata, error_summary }
 *
 * Frontend expects:
 *   { sessionId, message: { role, content, agentName, metadata } }
 */
function transformChatResponse(raw: unknown): ChatResponse {
  const data = (raw ?? {}) as Record<string, unknown>;

  const conversationId = (data.conversation_id ?? data.conversationId) as string | undefined;
  const status = data.status as ChatResponse['status'] | undefined;

  const responseText = (data.response ?? data.message ?? '') as string;
  const sessionId = (data.session_id ?? data.sessionId ?? '') as string;
  const agentName = (data.agent_name ?? data.agentName ?? '') as string;
  const meta = data.metadata as Record<string, unknown> | undefined;

  return {
    sessionId,
    message: {
      role: 'assistant',
      content: responseText,
      agentName: agentName || undefined,
      metadata: meta
        ? {
            latency: typeof meta.duration_ms === 'number' ? meta.duration_ms : undefined,
            taskStatus: (data.state as string) || undefined,
          }
        : undefined,
    },
    ...(conversationId && { conversationId }),
    ...(status && { status }),
  };
}

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    sendMessage: builder.mutation<ChatResponse, ChatRequest>({
      query: (body) => ({
        url: '/playground/chat',
        method: 'POST',
        body: {
          query: body.query,
          'agent-id': body.agentId,
          ...(body.sessionId && { 'session-id': body.sessionId }),
          ...(body.forceNewSession && { 'force-new-session': true }),
          ...(body.environment && { environment: body.environment }),
          ...(body.attachments && { attachments: body.attachments }),
        },
      }),
      transformResponse: (raw: unknown) => transformChatResponse(raw),
      invalidatesTags: ['Session'],
    }),
    getUploadUrl: builder.mutation<UploadUrlResponse, UploadUrlRequest>({
      query: (body) => ({
        url: '/files/upload-url',
        method: 'POST',
        body,
      }),
    }),
    getPlaygroundConversations: builder.query<
      { conversations: Array<{ sessionId: string; agentId: string; agentName: string; createdAt: string }> },
      { agentId?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: '/playground/conversations',
        params: {
          ...(params.agentId && { 'agent-id': params.agentId }),
          ...(params.page && { page: params.page }),
          ...(params.limit && { limit: params.limit }),
        },
      }),
      transformResponse: (raw: unknown) =>
        normalizeKeys<{ conversations: Array<{ sessionId: string; agentId: string; agentName: string; createdAt: string }> }>(raw),
    }),
    textToSpeech: builder.mutation<VoiceTtsResponse, VoiceTtsRequest>({
      query: (body) => ({
        url: '/playground/voice/tts',
        method: 'POST',
        body,
      }),
    }),
    speechToText: builder.mutation<VoiceSttResponse, VoiceSttRequest>({
      query: (body) => ({
        url: '/playground/voice/stt',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useSendMessageMutation,
  useGetUploadUrlMutation,
  useGetPlaygroundConversationsQuery,
  useTextToSpeechMutation,
  useSpeechToTextMutation,
} = chatApi;
